import { useState } from "react";

// Material UI
import {
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  MenuItem,
  Select,
  ListItemText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Clear, Visibility, VisibilityOff } from "@mui/icons-material";

// Libraries
import get from "lodash/get";

// Helpers
import {
  getFormikHelperTextState,
  getHelperTextWithAction,
} from "./formikHelperText";

const FormikTextField = ({ formik, field, name, ...rest }) => {
  const { slotProps: slotPropsFromProps = {}, ...textFieldProps } = rest;
  const rawValue = get(formik.values, name, field.multiple ? [] : "");
  const value = rawValue === null ? (field.multiple ? [] : "") : rawValue;
  const { touched, errorMsg, hasError, helperTextContent } =
    getFormikHelperTextState({
      formik,
      name,
      baseHelperText: textFieldProps.helperText ?? field.helperText,
      hideErrorText: field.hideErrorText,
      showHelperTextWithError: field.showHelperTextWithError,
    });
  const helperText = getHelperTextWithAction({
    helperTextContent,
    helperTextAction: field.helperTextAction,
  });
  const isRequired = field.required ?? true;
  const labelText =
    field.label && isRequired ? `${field.label} *` : field.label;

  const [menuOpen, setMenuOpen] = useState(false);
  const isPassword = field.type === "password";
  const [showPassword, setShowPassword] = useState(false);
  const isClearable = Boolean(field.clearable);
  const hasValue =
    field.multiple && Array.isArray(value) ? value.length > 0 : Boolean(value);

  const variant = field.variant || (field.review ? "standard" : "outlined");
  const inputType = isPassword
    ? showPassword
      ? "text"
      : "password"
    : field.type || "text";
  const fieldStyles = field.sx || {};

  const passwordAdornment = isPassword ? (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? "Hide password" : "Show password"}
        onClick={() => setShowPassword((s) => !s)}
        edge="end"
        size="small"
        sx={{ p: "0.25rem", mr: "0.25rem" }}
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  ) : null;

  const clearAdornment =
    isClearable && hasValue && !field.disabled && !field.review ? (
      <InputAdornment position="end">
        <IconButton
          aria-label={`Clear ${field.label || name}`}
          onClick={() => formik.setFieldValue(name, field.multiple ? [] : "")}
          edge="end"
          size="small"
          sx={{ p: "0.25rem", mr: "0.25rem" }}
        >
          <Clear fontSize="small" />
        </IconButton>
      </InputAdornment>
    ) : null;

  const existingEndAdornment =
    field.slotProps?.input?.endAdornment ??
    slotPropsFromProps.input?.endAdornment;

  const composedEndAdornment = (
    <>
      {clearAdornment}
      {passwordAdornment}
      {existingEndAdornment}
    </>
  );
  const helperTextProps = textFieldProps.FormHelperTextProps;
  const mergedHelperTextProps = {
    ...helperTextProps,
    component: "div",
    sx: { whiteSpace: "pre-line", ...(helperTextProps?.sx || {}) },
  };
  const mergedSlotProps = {
    ...field.slotProps,
    ...slotPropsFromProps,
  };
  const textFieldSlotProps = {
    ...mergedSlotProps,
    input: {
      ...field.slotProps?.input,
      ...slotPropsFromProps.input,
      endAdornment: composedEndAdornment,
    },
  };

  const handleSelectChange = (event, field) => {
    const { value } = event.target;
    if (field.review) return;
    if (field.multiple) {
      formik.setFieldValue(name, Array.isArray(value) ? value : [value]);
    } else {
      formik.setFieldValue(name, value);
    }
  };

  // Render multiple if applicable
  if (field.type === "select") {
    return (
      <FormControl
        fullWidth
        sx={fieldStyles}
        variant={variant}
        error={hasError}
      >
        <InputLabel id={`${name}-label`}>{labelText}</InputLabel>
        <Select
          labelId={`${name}-label`}
          id={name}
          name={name}
          label={labelText}
          slotProps={mergedSlotProps}
          open={field.review ? false : menuOpen}
          onOpen={() => {
            if (!field.review) setMenuOpen(true);
          }}
          onClose={() => setMenuOpen(false)}
          multiple={field.multiple}
          value={value}
          onChange={
            field.onChange
              ? (event) => field.onChange(event, formik)
              : (event) => handleSelectChange(event, field)
          }
          onBlur={formik.handleBlur}
          error={hasError}
          renderValue={(selected) => {
            // if (field.review) return selected;
            if (Array.isArray(selected)) {
              // Multiple select
              if (selected.length === 0) return field.label;
              return field.options
                .filter((option) => selected.includes(option.value))
                .map((option) => option.label)
                .join(", ");
            } else {
              // Single select
              const option = field.options?.find(
                (option) => option.value === selected
              );
              return option ? option.label : field.label;
            }
          }}
          sx={{ width: "100%", textAlign: "left" }}
          {...textFieldProps}
        >
          {field.options?.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={field.review}
            >
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
        {touched && errorMsg && <FormHelperText>{errorMsg}</FormHelperText>}
      </FormControl>
    );
  }

  // Render regular TextField if its text or other type
  return (
    <TextField
      id={name}
      name={name}
      value={value}
      label={labelText}
      type={inputType}
      variant={variant}
      disabled={field.disabled}
      autoComplete={field.autoComplete || "off"}
      rows={8}
      multiline={field.multiline}
      onChange={
        field.onChange ? (e) => field.onChange(e, formik) : formik.handleChange
      }
      onBlur={field.onBlur ? (e) => field.onBlur(e, formik) : formik.handleBlur}
      error={hasError}
      helperText={helperText}
      slotProps={{
        ...textFieldSlotProps,
        formHelperText: mergedHelperTextProps,
      }}
      sx={{ width: "100%", ...fieldStyles }}
      {...textFieldProps}
    >
      {field.info}
    </TextField>
  );
};

export default FormikTextField;
